import { Link } from "react-router-dom"
import {ArrowRight, Ticket, Clock, MessageSquare, FileText, Bell, Search, ChevronDown, ChevronUp, Shield, Zap, Users} from 'lucide-react'
import SectionHeading from "@/components/SectionHeading"
import InlineEdit from "@/components/InlineEdit"
import { useState } from "react"

const processSteps = [
  { key: "support_step_1", step: "1", title: "Ouvrir un ticket", desc: "Décrivez votre problème avec le maximum de détails et joignez des captures d'écran si nécessaire." },
  { key: "support_step_2", step: "2", title: "Prise en charge", desc: "Un technicien analyse votre demande et vous contacte dans les meilleurs délais." },
  { key: "support_step_3", step: "3", title: "Résolution", desc: "Nous intervenons à distance ou sur site selon la nature du problème." },
  { key: "support_step_4", step: "4", title: "Suivi & Clôture", desc: "Vous êtes notifié à chaque étape. Le ticket est clôturé une fois le problème résolu." },
]

const requestTypes = [
  { key: "support_type_1", icon: Zap, title: "Demande technique", desc: "Problème informatique, panne, dysfonctionnement — ouvrez un ticket de support.", color: "text-cyan-400", bg: "bg-cyan-500/10" },
  { key: "support_type_2", icon: Users, title: "Demande commerciale", desc: "Devis, nouveau contrat, question sur nos offres — utilisez le formulaire de contact.", color: "text-emerald-400", bg: "bg-emerald-500/10" },
  { key: "support_type_3", icon: Shield, title: "Urgence critique", desc: "Serveur en panne, faille de sécurité, perte de données — contactez-nous par téléphone.", color: "text-red-400", bg: "bg-red-500/10" },
]

const defaultFaq = [
  { key: "support_faq_1", q: "Comment créer un ticket de support ?", a: "Connectez-vous à votre espace client, puis cliquez sur « Nouveau ticket ». Remplissez le formulaire avec le maximum de détails sur votre problème." },
  { key: "support_faq_2", q: "Quel est le délai de prise en charge ?", a: "Les tickets de priorité normale sont pris en charge sous 2 heures ouvrées. Les urgences sont traitées immédiatement." },
  { key: "support_faq_3", q: "Puis-je joindre des fichiers à mon ticket ?", a: "Oui, vous pouvez joindre des captures d'écran, documents et fichiers lors de la création du ticket ou dans les réponses." },
  { key: "support_faq_4", q: "Comment suivre l'avancement de ma demande ?", a: "Depuis votre espace client, consultez la liste de vos tickets. Chaque ticket affiche son statut en temps réel et vous recevez des notifications par email." },
  { key: "support_faq_5", q: "Dois-je avoir un contrat pour ouvrir un ticket ?", a: "Non, tout client peut ouvrir un ticket. Cependant, les clients sous contrat bénéficient de délais de prise en charge garantis et d'une priorité accrue." },
]

export default function SupportPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null)

  return (
    <div className="min-h-screen pt-24 lg:pt-32">
      {/* Header */}
      <section className="section-padding pb-16">
        <SectionHeading
          badge="Support"
          title="Centre de"
          highlight="support technique"
          description="Centralisez vos demandes, suivez leur avancement et communiquez directement avec notre équipe technique."
        />
        <div className="flex flex-wrap justify-center gap-4 mt-8">
          <Link to="/espace-client/nouveau-ticket" className="btn-primary text-base !py-3.5 !px-8">
            <Ticket className="w-5 h-5" />
            Créer un ticket
          </Link>
          <Link to="/espace-client" className="btn-secondary text-base !py-3.5 !px-8">
            Mon espace client
          </Link>
        </div>
      </section>

      {/* Types de demandes */}
      <section className="section-padding py-16">
        <h2 className="text-2xl font-bold text-white text-center mb-8">Quel type de demande ?</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {requestTypes.map((type, i) => (
            <div key={type.key} className="card-hover-lift animate-fade-in-up" style={{ animationDelay: `${0.1 * i}s` }}>
              <div className={`w-11 h-11 rounded-xl ${type.bg} flex items-center justify-center mb-4`}>
                <type.icon className={`w-5 h-5 ${type.color}`} />
              </div>
              <h3 className="text-base font-semibold text-white mb-2">
                <InlineEdit contentKey={`${type.key}_title`} defaultValue={type.title} className="text-base font-semibold text-white" />
              </h3>
              <InlineEdit contentKey={`${type.key}_desc`} defaultValue={type.desc} as="p" className="text-sm text-slate-400 leading-relaxed" multiline />
            </div>
          ))}
        </div>
      </section>

      {/* Process */}
      <section className="section-padding py-20 lg:py-28">
        <SectionHeading
          badge="Processus"
          title="Comment ça"
          highlight="fonctionne"
          description="De l'ouverture à la résolution, voici les étapes de traitement de votre demande."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {processSteps.map((step, i) => (
            <div key={step.key} className="card text-center animate-fade-in-up" style={{ animationDelay: `${0.1 * i}s` }}>
              <div className="w-12 h-12 rounded-full bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center mx-auto mb-4">
                <span className="text-lg font-bold text-cyan-400">{step.step}</span>
              </div>
              <h4 className="font-semibold text-white mb-2">
                <InlineEdit contentKey={`${step.key}_title`} defaultValue={step.title} className="font-semibold text-white" />
              </h4>
              <InlineEdit contentKey={`${step.key}_desc`} defaultValue={step.desc} as="p" className="text-xs text-slate-400 leading-relaxed" multiline />
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="section-padding py-16 border-t border-slate-800/50">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { key: "support_feat_1", icon: Clock, title: "Réponse rapide", desc: "Prise en charge sous 2h ouvrées" },
            { key: "support_feat_2", icon: MessageSquare, title: "Échanges centralisés", desc: "Tout l'historique au même endroit" },
            { key: "support_feat_3", icon: FileText, title: "Pièces jointes", desc: "Captures d'écran et documents" },
            { key: "support_feat_4", icon: Bell, title: "Notifications", desc: "Alertes email à chaque mise à jour" },
          ].map((feat, i) => (
            <div key={feat.key} className="flex items-start gap-3 p-4 rounded-xl bg-slate-900/40 border border-slate-800/50 animate-fade-in-up" style={{ animationDelay: `${0.05 * i}s` }}>
              <feat.icon className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" />
              <div>
                <InlineEdit contentKey={`${feat.key}_title`} defaultValue={feat.title} className="text-sm font-semibold text-white" />
                <InlineEdit contentKey={`${feat.key}_desc`} defaultValue={feat.desc} as="p" className="text-xs text-slate-400 mt-0.5" />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding py-20 lg:py-28">
        <SectionHeading
          badge="FAQ"
          title="Questions"
          highlight="fréquentes"
          description="Consultez les réponses aux questions les plus courantes avant d'ouvrir un ticket."
        />
        <div className="max-w-2xl mx-auto space-y-3">
          {defaultFaq.map((item, i) => (
            <div key={item.key} className="card !p-0 overflow-hidden animate-fade-in-up" style={{ animationDelay: `${0.05 * i}s` }}>
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-800/30 transition-colors"
              >
                <InlineEdit contentKey={`${item.key}_q`} defaultValue={item.q} as="span" className="text-sm font-medium text-white pr-4" />
                {openFaq === i ? (
                  <ChevronUp className="w-4 h-4 text-cyan-400 shrink-0" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" />
                )}
              </button>
              {openFaq === i && (
                <div className="px-5 pb-5 border-t border-slate-800/50 pt-4">
                  <InlineEdit contentKey={`${item.key}_a`} defaultValue={item.a} as="p" className="text-sm text-slate-400 leading-relaxed" multiline />
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding py-16">
        <div className="card !p-10 text-center bg-gradient-to-br from-cyan-500/5 to-emerald-500/5">
          <InlineEdit
            contentKey="support_cta_title"
            defaultValue="Prêt à soumettre votre demande ?"
            as="h2"
            className="text-2xl font-bold text-white mb-3"
          />
          <InlineEdit
            contentKey="support_cta_desc"
            defaultValue="Créez un ticket en quelques clics et notre équipe s'en occupe."
            as="p"
            className="text-slate-400 mb-6"
          />
          <Link to="/espace-client/nouveau-ticket" className="btn-primary">
            <Ticket className="w-4 h-4" />
            Créer un ticket maintenant
          </Link>
        </div>
      </section>
    </div>
  )
}
